import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { it } from 'date-fns/locale';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  locale = it,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={locale}
      className={cn('relative p-3', className)}
      classNames={{
        months: 'flex flex-col gap-4 sm:flex-row',
        month: 'relative flex flex-col gap-4',
        month_caption: 'flex h-7 items-center justify-center',
        caption_label: 'text-sm font-medium capitalize select-none',
        dropdowns: 'flex items-center gap-1.5 text-sm font-medium',
        dropdown_root:
          'relative inline-flex items-center rounded-md border border-(--color-border) px-2 py-1 hover:bg-(--color-muted) focus-within:ring-2 focus-within:ring-(--color-ring)',
        dropdown: 'absolute inset-0 opacity-0 cursor-pointer',
        nav: 'absolute inset-x-3 top-3 flex items-center justify-between pointer-events-none [&>button]:pointer-events-auto',
        button_previous: cn(
          buttonVariants({ variant: 'outline', size: 'icon' }),
          'h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100',
        ),
        button_next: cn(
          buttonVariants({ variant: 'outline', size: 'icon' }),
          'h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100',
        ),
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday:
          'w-9 rounded-md text-[0.8rem] font-normal text-(--color-muted-foreground) capitalize',
        week: 'mt-2 flex w-full',
        day: 'relative h-9 w-9 p-0 text-center text-sm focus-within:relative focus-within:z-20',
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 p-0 font-normal aria-selected:opacity-100',
        ),
        range_start: 'day-range-start rounded-l-md',
        range_end: 'day-range-end rounded-r-md',
        selected:
          '[&>button]:bg-(--color-primary) [&>button]:text-(--color-primary-foreground) [&>button]:hover:bg-(--color-primary) [&>button]:hover:text-(--color-primary-foreground)',
        today:
          '[&:not(.rdp-selected)>button]:bg-(--color-accent) [&:not(.rdp-selected)>button]:text-(--color-accent-foreground)',
        outside: 'text-(--color-muted-foreground) opacity-50',
        disabled: 'text-(--color-muted-foreground) opacity-50',
        range_middle:
          '[&>button]:bg-(--color-accent) [&>button]:text-(--color-accent-foreground) [&>button]:rounded-none',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: chevronClassName }) => {
          if (orientation === 'left') return <ChevronLeft className={cn('h-4 w-4', chevronClassName)} />;
          if (orientation === 'right') return <ChevronRight className={cn('h-4 w-4', chevronClassName)} />;
          return <></>;
        },
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';
